import { useCallback, useEffect } from 'react';

import { createFocusManager } from '@react-aria/focus';

import { useBrowserEventListener } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Covers the gaps in the dialog's focus handling:
 * - Route content can be committed before it is visible, so the scope's
 *   one-time auto focus finds nothing to focus.
 * - React Native Web text inputs stop their key events from bubbling, so the
 *   scope never sees Tab, and window listeners never see Escape.
 * - Full screen dialogs can keep their controls in the header or next to the
 *   dialog, so instead of trapping focus, the hidden screens behind the route
 *   are made inert.
 */
export const useDialogFocus = (params: Params) => {
  const { dialogId, isEnabled, isFullScreen, onClose } = params;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const focusDialogWhenVisible = useCallback(() => {
    const dialogElement = document.getElementById(dialogId);

    if (!isEnabled || !dialogElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const isVisible = dialogElement.offsetWidth > 0;

      if (!isVisible) {
        return;
      }

      resizeObserver.disconnect();

      const isFocusInsideDialog = checkIfInsideElement(dialogElement, document.activeElement);

      if (isFocusInsideDialog) {
        return;
      }

      focusFirstInDialog(dialogElement);
    });

    resizeObserver.observe(dialogElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [dialogId, isEnabled]);

  const makeHiddenScreensInert = useCallback(() => {
    const dialogElement = document.getElementById(dialogId);

    if (!isEnabled || !isFullScreen || !dialogElement) {
      return;
    }

    const hiddenScreens = getHiddenScreens(dialogElement);

    hiddenScreens.forEach(addInert);

    return () => {
      hiddenScreens.forEach(removeInert);
    };
  }, [dialogId, isEnabled, isFullScreen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.isComposing) {
        return;
      }

      const { target } = event;

      const dialogElement = document.getElementById(dialogId);
      const isEventFromDialog = checkIfEventFromDialog({ dialogElement, isFullScreen, target });

      if (!dialogElement || !isEventFromDialog) {
        return;
      }

      if (event.key === 'Escape') {
        onClose?.();

        return;
      }

      const shouldWrapTab = !isFullScreen && event.key === 'Tab' && checkIfTextField(target);

      if (!shouldWrapTab) {
        return;
      }

      event.preventDefault();

      const focusManager = createFocusManager({ current: dialogElement });
      const focusOptions = { from: target, tabbable: true, wrap: true };

      if (event.shiftKey) {
        focusManager.focusPrevious(focusOptions);

        return;
      }

      focusManager.focusNext(focusOptions);
    },
    [dialogId, isFullScreen, onClose],
  );

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(focusDialogWhenVisible, [focusDialogWhenVisible]);

  useEffect(makeHiddenScreensInert, [makeHiddenScreensInert]);

  useBrowserEventListener(window, 'keydown', handleKeyDown, CAPTURE_LISTENER_OPTIONS, !isEnabled);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function focusFirstInDialog(dialogElement: HTMLElement) {
  const focusManager = createFocusManager({ current: dialogElement });
  const firstTabbableElement = focusManager.focusFirst({ tabbable: true });

  if (firstTabbableElement) {
    return;
  }

  dialogElement.focus();
}

/**
 * Navigators hide inactive screens from screen readers with `aria-hidden`, but
 * keyboard focus can still reach them, so those are the screens to make inert.
 */
function getHiddenScreens(element: Element): Element[] {
  const { parentElement } = element;

  if (!parentElement || parentElement === document.body) {
    return [];
  }

  const hiddenSiblings = Array.from(parentElement.children).filter(checkIfHiddenScreen);

  return [...hiddenSiblings, ...getHiddenScreens(parentElement)];
}

function checkIfHiddenScreen(element: Element) {
  return element.getAttribute('aria-hidden') === 'true' && !element.hasAttribute('inert');
}

function addInert(element: Element) {
  element.setAttribute('inert', '');
}

function removeInert(element: Element) {
  element.removeAttribute('inert');
}

function checkIfEventFromDialog(params: EventSourceParams) {
  const { dialogElement, isFullScreen, target } = params;

  const isFocusLost = target === document.body;

  if (isFocusLost) {
    return true;
  }

  // NOTE: Behind a full screen dialog everything is inert, so only nested
  //       modals, which handle their own Escape, are excluded.
  if (isFullScreen) {
    return !checkIfInsideNestedModal(target);
  }

  return checkIfInsideElement(dialogElement, target);
}

function checkIfInsideNestedModal(target: EventTarget | null) {
  return target instanceof Element && target.closest(NESTED_MODAL_SELECTOR) !== null;
}

function checkIfInsideElement(element: HTMLElement | null, target: EventTarget | null) {
  return target instanceof Node && (element?.contains(target) ?? false);
}

function checkIfTextField(
  target: EventTarget | null,
): target is HTMLInputElement | HTMLTextAreaElement {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CAPTURE_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true };

const NESTED_MODAL_SELECTOR = '[role="dialog"][aria-modal="true"]';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  dialogId: string;
  isEnabled: boolean;
  isFullScreen: boolean;
  onClose?: () => void;
};

type EventSourceParams = {
  dialogElement: HTMLElement | null;
  isFullScreen: boolean;
  target: EventTarget | null;
};
